import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter, Route, Switch } from 'react-router-dom';
import styled from 'styled-components';
import pathToRegexp from 'path-to-regexp';
import { ParallaxContext } from '../parallax/components';
import { SpaceScene } from '../parallax/webgl';
import { Menu, MenuTransitionContext } from '../navigation/components';
import {
  AppFooter,
  BackgroundStripes,
  BlockingLoader,
  CookiePolicy,
  ScrollToTop,
} from './components';
import {
  AboutPage,
  CareersPage,
  GreenPage,
  CareersSubPage,
  CareersOpenPositionPage,
  GamesPage,
  GenericPage,
  HomePage,
  NewsEventsPage,
  PressReleasePage,
  TeamPage,
} from '../pages';

import { Modal, ModalContent } from '../pages/components';
import { BioBrowser } from '../people/components';
import * as navigationActions from '../navigation/navigation.actions';

const url = require('url');

const preliminaryWebglCheck = process.env.REACT_APP_MODE === 'webgl';

const WebglEnabled = preliminaryWebglCheck;

const SpaceBackground = styled.div`
  width: 100vw;
  height: 100vh;
  position: fixed;
  background-image: ${
    !WebglEnabled
      ? `url(${process.env.PUBLIC_URL}/img/MenuWatermark_BW.png);`
      : `url(${process.env.PUBLIC_URL}/webgl_monochrome/bg.jpg);`
  }
  background-repeat: no-repeat;
  background-size: cover;
  background-position: 50% 50%;
  transform: translate3d(0, 0, 0);
`;

const SpaceBackgroundWhite = styled(SpaceBackground)`
  background-image: url(${process.env.PUBLIC_URL}/img/MenuWatermark_W.png);
`;

class ConnectedWrapper extends React.PureComponent {
  progress = 0;

  static propTypes = {
    breakpoint: PropTypes.string.isRequired,
    children: PropTypes.any,
  };

  static contextTypes = {
    registerMenuTransitionElement: PropTypes.func.isRequired,
    unregisterMenuTransitionElement: PropTypes.func.isRequired,
  };

  componentDidUpdate = prevProps => {
    if (prevProps.breakpoint !== this.props.breakpoint) {
      this.checkMenuTransitionContextNeeded();
    }
  };

  componentDidMount = () => {
    this.checkMenuTransitionContextNeeded();
  };

  checkMenuTransitionContextNeeded = () => {
    const { breakpoint } = this.props;
    if (WebglEnabled || breakpoint === 'phone') {
      this.context.registerMenuTransitionElement(this.updateProgress);
    } else {
      this.context.unregisterMenuTransitionElement(this.updateProgress);
    }
  };

  updateProgress = val => {
    this.el.style.opacity = 1 - Math.max(0, Math.min(1, val.toFixed(2) * 2));
    this.progress = val;
  };

  boundRef = el => {
    this.el = el;
  };

  render() {
    const { children } = this.props;
    const styleObj = {
      position: 'relative',
      zIndex: 1,
    };
    return (
      <div ref={this.boundRef} style={styleObj}>
        {!WebglEnabled && <SpaceBackgroundWhite />}
        {children}
      </div>
    );
  }
}

class App extends Component {
  static propTypes = {
    breakpoint: PropTypes.string.isRequired,
    contentLoaded: PropTypes.bool.isRequired,
    location: PropTypes.object.isRequired,
    navigate: PropTypes.func.isRequired,
  };

  state = {
    webGlLoaded: WebglEnabled ? false : true,
    displayCookiePolicyModal:
      localStorage && !localStorage.getItem('sag-cookies-modal-seen'),
  };

  previousLocation = null;

  componentDidMount = () => {
    document.addEventListener('click', this.handleInternalLinks, true);
  };

  componentWillUpdate(nextProps, nextState) {
    const { location } = this.props;

    if (nextProps.history.action !== 'POP' && !this.isModal()) {
      this.previousLocation = location;
    }
  }

  onWebGlLoaded = () => {
    this.setState({
      webGlLoaded: true,
    });
  };

  handleInternalLinks = ev => {
    const { navigate, location } = this.props;
    if (
      ev.altKey ||
      ev.ctrlKey ||
      ev.metaKey ||
      ev.shiftKey ||
      ev.defaultPrevented
    ) {
      return true;
    }

    let anchor = null;
    for (let n = ev.target; n.parentNode; n = n.parentNode) {
      if (n.nodeName === 'A') {
        anchor = n;
        break;
      }
    }
    if (!anchor) return true;

    const href = anchor.getAttribute('href');
    if (!href) return;
    const u = url.parse(anchor.getAttribute('href'));

    if (u.host && u.host !== location.host) return true;

    ev.preventDefault();

    navigate(url.resolve(location.pathname, u.path || '') + (u.hash || ''));
    return false;
  };

  isModal = () => {
    const { location } = this.props;
    const re = pathToRegexp('/team/:person');
    const result = re.exec(location.pathname);

    return result !== null;
  };

  onModalClose = () => {
    const { navigate } = this.props;
    navigate(
      this.previousLocation ? this.previousLocation.pathname : '/team',
      true
    );
  };

  onCookiePolicyClick = () => {
    localStorage && localStorage.setItem('sag-cookies-modal-seen', Date.now());
    this.setState({
      displayCookiePolicyModal: false,
    });
  };

  render() {
    const { webGlLoaded, displayCookiePolicyModal } = this.state;
    const { location, contentLoaded, breakpoint } = this.props;
    const isLoading = !webGlLoaded || !contentLoaded;
    const isModal = this.isModal();
    return (
      <ParallaxContext>
        <ScrollToTop />
        <MenuTransitionContext>
          {WebglEnabled ? (
            <SpaceScene onWebGlLoaded={this.onWebGlLoaded} />
          ) : (
            breakpoint === 'phone' && <SpaceBackground />
          )}
          <Menu barEnabled={!WebglEnabled} />
          <ConnectedWrapper breakpoint={breakpoint}>
            <BackgroundStripes color={'rgba(59, 60, 67, .12)'} />
            <Switch
              location={
                isModal && this.previousLocation
                  ? this.previousLocation
                  : location
              }
            >
              <Route
                path="/careers/:category/:openposition"
                component={CareersOpenPositionPage}
              />
              <Route path="/careers/:category" component={CareersSubPage} />
              <Route path="/careers" component={CareersPage} />
              <Route path="/green" component={GreenPage} />
              <Route path="/about" component={AboutPage} />
              <Route path="/games" component={GamesPage} />
              <Route path="/news-events/:slug" component={PressReleasePage} />
              <Route path="/news-events" component={NewsEventsPage} />
              <Route path="/team" exact component={TeamPage} />
              <Route path="/:slug" component={GenericPage} />
              <Route path="/" component={HomePage} />
            </Switch>
            <Route
              path="/team/:person"
              render={() => (
                <Modal>
                  <ModalContent onClose={this.onModalClose}>
                    <BioBrowser />
                  </ModalContent>
                </Modal>
              )}
            />
            <AppFooter />
          </ConnectedWrapper>
        </MenuTransitionContext>
        {displayCookiePolicyModal && (
          <CookiePolicy onGotItClick={this.onCookiePolicyClick} />
        )}
        <BlockingLoader isLoading={isLoading} />
      </ParallaxContext>
    );
  }
}

const mapStateToProps = state => {
  const { breakpoint } = state.layout;
  const contentLoaded = state.entities.content.updated.timestamp !== null;
  return {
    breakpoint,
    contentLoaded,
  };
};

export default withRouter(connect(mapStateToProps, navigationActions)(App));



// WEBPACK FOOTER //
// ./src/modules/core/App.js